import Upload from 'components/Upload'
import React, { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import MedicalRecordsService from 'services/medicalRecords'
import FileList, { FileItemProps } from '../FileList'
import * as S from './styles'
import { useMedicalRecordForm } from '../../hooks/useMedicalRecordForm'
import { LoadingSpinner } from '../LoadingSpinner'
import { MedicalRecordFile } from 'models/MedicalRecord'

enum FILE_SIZE_ERROR_MESSAGES {
  TOTAL_SIZE_EXCEEDED = 'O total de 20 MB foi atingido. Exclua um dos arquivos anexados ou o substitua por um menor.',
  FILE_SIZE_EXCEEDED = 'O arquivo selecionado é maior que 20 MB. Tente novamente.',
}

export const DocumentUpload = () => {
  const { form } = useMedicalRecordForm()
  const { setValue } = useFormContext()

  const [uploadLoading, setUploadLoading] = useState(false)
  const defaultFiles: FileItemProps[] = form?.files?.map(file => ({
    id: file.uuid,
    name: file.original_name,
    size: file.size,
    preview: file.url,
    file: {size: file.size } as File
  }))
  const [uploadedFiles, setUploadedFiles] = useState<FileItemProps[]>(defaultFiles ?? [])
  const [errorUpload, setErrorUpload] = useState<string>('')

  const getTotalFileSizeInMegaBytes = (files: File[]): number => {
    return files.reduce((acc, file) => acc + file?.size, 0) / (1024 * 1024)
  }

  const handleUpload = async (files: File[]) => {
    setUploadLoading(true)
    const currentFilesSizeInMegaBytes = getTotalFileSizeInMegaBytes(
      uploadedFiles.map((uploadedFile) => uploadedFile.file)
    )
    const totalSizeFiles =
      getTotalFileSizeInMegaBytes(files) + currentFilesSizeInMegaBytes
    let errorMessage = ''
    if (totalSizeFiles > 20) {
      errorMessage =
        uploadedFiles.length > 0
          ? FILE_SIZE_ERROR_MESSAGES.TOTAL_SIZE_EXCEEDED
          : FILE_SIZE_ERROR_MESSAGES.FILE_SIZE_EXCEEDED
      setErrorUpload(errorMessage)
      return
    }

    const uploadPromises: Promise<FileItemProps | null>[] = []
    files.forEach(async (file) => {
      const filePayload = new FormData()
      filePayload.append('file', file)
      const { uuid } = form
      const promise = MedicalRecordsService
        .uploadFiles(uuid, filePayload)
        .then(
          (filesUploaded) => ({
            id: filesUploaded.uuid,
            file,
            preview: URL.createObjectURL(file),
            name: file.name,
            size: file.size,
            type: file.type,
            progress: 0,
            uploaded: false,
            error: false,
          } as FileItemProps)
        )
        .catch(() => {
          errorMessage = FILE_SIZE_ERROR_MESSAGES.FILE_SIZE_EXCEEDED
          return null
        })
      uploadPromises.push(promise)
    })

    const results = await Promise.allSettled(uploadPromises)
    const newFiles = results
      .filter((result) => result.status === 'fulfilled')
      .map((result) => (result as PromiseFulfilledResult<FileItemProps>).value)
      .filter((value) => value !== null) as FileItemProps[]

    const contactedFiles = uploadedFiles.concat(newFiles)

    const filesDraft = contactedFiles.map(file => {
      return {
        original_name: file.name,
        size: file.size,
        uuid: file.id,
        url: file.preview,
      } as MedicalRecordFile
    })

    setValue('files', filesDraft)

    setUploadedFiles(contactedFiles)
    setErrorUpload(errorMessage)
    setUploadLoading(false)
  }

  const handleDeleteFile = async (id: string) => {
    try {
      await MedicalRecordsService.deleteUploadFile(id)

      setUploadedFiles((files) => {
        const newFiles = files.filter((file) => file.id !== id)

        const newFilesDraft = newFiles.map(file => {
          return {
            original_name: file.name,
            size: file.size,
            uuid: file.id,
            url: file.preview,
          } as MedicalRecordFile
        }) 
        
        setValue('files', newFilesDraft)

        return newFiles
      })
      setErrorUpload('')
    } catch (error) {
      setErrorUpload('Não foi possível deletar o arquivo.')
    }
  }

  return (
    <S.Container>
      <S.WrapperList>
        {!!uploadedFiles.length && (
          <FileList files={uploadedFiles} onDelete={handleDeleteFile} />
        )}
        {uploadLoading && (
          <LoadingSpinner />
        )}
      </S.WrapperList>
      <Upload onUpload={handleUpload} error={errorUpload} />
    </S.Container>
  )
}
